import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (reset: () => void, error: Error) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Dev-time only console output. Wire up Sentry / OTel here later.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(this.reset, error);

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'var(--bg-canvas)',
        }}
      >
        <div
          style={{
            maxWidth: 520,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-lg)',
            padding: 28,
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <div className="kuun-h3" style={{ marginBottom: 8 }}>
            예기치 않은 오류가 발생했습니다.
          </div>
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            {error.message}
          </div>
          <pre
            style={{
              marginTop: 12,
              maxHeight: 200,
              overflow: 'auto',
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--fg-muted)',
              background: 'var(--bg-sunken)',
              padding: 10,
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {error.stack ?? ''}
          </pre>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              onClick={this.reset}
              style={{
                height: 32,
                padding: '0 14px',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--brand)',
                color: 'var(--brand-on)',
                fontWeight: 500,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              다시 시도
            </button>
            <button
              onClick={() => window.location.assign('/')}
              style={{
                height: 32,
                padding: '0 14px',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg-surface)',
                color: 'var(--fg-default)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              대시보드로
            </button>
          </div>
        </div>
      </div>
    );
  }
}
